import Schedule from '@mui/icons-material/Schedule';
import Sensors from '@mui/icons-material/Sensors';
import SensorsOff from '@mui/icons-material/SensorsOff';
import { useTheme } from '@mui/material';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { GpsSignalStatus, GpsStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

interface GpsStatusIconProps {
  gpsStatus: GpsStatus;
  sx?: SxProps;
  onMouseEnter?: (e: React.MouseEvent<SVGSVGElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<SVGSVGElement>) => void;
  showIdleBadge?: boolean;
  hasLocationAccuracyError?: boolean;
}

const getStatusColor = (
  gpsStatus: GpsStatus,
  theme: Theme,
  hasLocationAccuracyError: boolean,
): string => {
  if (hasLocationAccuracyError) {
    return theme.palette.brandV2Red.main;
  }

  switch (gpsStatus.status) {
    case GpsSignalStatus.ONLINE:
      return theme.palette.brandV2Green.main;
    case GpsSignalStatus.OFFLINE:
      return theme.brandV2.colors.treadGray5;
    case GpsSignalStatus.NO_SIGNAL:
      return theme.palette.brandV2Red.main;
    default:
      return theme.brandV2.colors.treadGray5;
  }
};

const getStatusTestId = (isOnline: boolean, hasLocationAccuracyError: boolean) => {
  if (hasLocationAccuracyError) {
    return 'gps-status-icon-error';
  }

  return isOnline ? 'gps-status-icon-online' : 'gps-status-icon-offline';
};

export const GpsStatusText = ({
  gpsStatus,
  sx,
  hasLocationAccuracyError = false,
}: {
  gpsStatus: GpsStatus;
  sx?: SxProps;
  hasLocationAccuracyError?: boolean;
}) => {
  const theme = useTheme();
  const color = getStatusColor(gpsStatus, theme, hasLocationAccuracyError);
  const formattedStatus = gpsStatus.status.replace(/_/g, ' ').toLowerCase();
  const label = hasLocationAccuracyError
    ? t('live_map.location_data.gps_status_error')
    : formattedStatus.charAt(0).toUpperCase() + formattedStatus.slice(1);

  return <Typography sx={{ color, ...sx }}>{label}</Typography>;
};

export const IdleIcon = ({ size = 14 }: { size?: number }) => {
  const theme = useTheme();

  return (
    <Schedule
      data-test-id="idle-icon"
      sx={{
        color: theme.palette.brandV2OrangeDark.main,
        fontSize: `${size}px`,
      }}
    />
  );
};

export default function GpsStatusIcon({
  gpsStatus,
  sx,
  onMouseEnter,
  onMouseLeave,
  showIdleBadge = true,
  hasLocationAccuracyError = false,
}: GpsStatusIconProps) {
  const theme = useTheme();
  const isOnline = gpsStatus.status === GpsSignalStatus.ONLINE;
  const isIdle = isOnline && gpsStatus.idle;
  const IconComponent = isOnline ? Sensors : SensorsOff;
  const color = getStatusColor(gpsStatus, theme, hasLocationAccuracyError);

  const testId = getStatusTestId(isOnline, hasLocationAccuracyError);

  const icon = (
    <IconComponent
      data-test-id={testId}
      sx={{ color, fontSize: '12px', height: 20, width: 20, ...sx }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );

  if (!isIdle || !showIdleBadge) {
    return icon;
  }

  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      badgeContent={
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            borderRadius: '50%',
            display: 'flex',
            height: '14px',
            width: '14px',
            justifyContent: 'center',
          }}
        >
          <IdleIcon size={13} />
        </Box>
      }
      sx={{
        '& .MuiBadge-badge': {
          height: 'auto',
          minWidth: 0,
          padding: 0,
          zIndex: 1,
        },
      }}
    >
      {icon}
    </Badge>
  );
}
